import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Inbox, Check, X, AlertCircle, Loader2, Clock } from 'lucide-react';
import { api, parseApiError } from '../lib/api';
import { Card } from './Card';
import { cn } from '../lib/cn';
import { SETTLE, settleDelay } from '../lib/motion';

// ─────────────────────────────────────────────────────────────────────────────
// ClanCreateRequestsPanel — T3 (server-is-the-org, 2026-07-19): the org-admin
// inbox for GATE-flow "apply to create a clan" applications (distinct from
// components/ClanApplicationsPanel.tsx, which is a CLAN leader's inbox for
// players applying to JOIN that specific clan — different backend, different
// audience). Sits beside OrgClansPanel in TournamentSetup's OrgPane; mirrors
// its "pending join requests" row idiom (Clock icon, emerald/red bordered
// action pills) plus IncomingRoleRequestsPanel's inline resolver-note-on-deny
// pattern (never a browser prompt()).
//
// `canManage` is the SAME explicit org-role gate OrgClansPanel/OrgMembersPanel
// use (org role, not platform role — lib/orgAccess.ts) — the caller computes
// it from currentRole/isFounder; this panel just double-guards on it, same as
// its siblings. OrgPane itself is already reachable only by a platform
// canWrite role or an ORG_ADMIN/ORG_OWNER of THIS org (TournamentSetup's own
// top-level gate), so this is a belt-and-braces re-check, not the only gate.
// ─────────────────────────────────────────────────────────────────────────────

function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

export function ClanCreateRequestsPanel({
  orgId,
  canManage,
}: {
  orgId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [noteByReq, setNoteByReq] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['clanCreateRequests', orgId],
    queryFn: () => api.listClanCreateRequests(orgId, 'PENDING'),
    enabled: canManage,
    refetchInterval: 30_000,
  });

  const approveMut = useMutation({
    mutationFn: (requestId: string) => api.approveClanCreateRequest(orgId, requestId),
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['clanCreateRequests', orgId] });
      // A fresh clan just landed under this org — the Affiliated Clans panel
      // reads the same org-scoped clan list.
      qc.invalidateQueries({ queryKey: ['orgClans', orgId] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const denyMut = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      api.denyClanCreateRequest(orgId, requestId, reason),
    onSuccess: (_res, vars) => {
      setError('');
      setNoteByReq(s => {
        const next = { ...s };
        delete next[vars.requestId];
        return next;
      });
      qc.invalidateQueries({ queryKey: ['clanCreateRequests', orgId] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Guard: non-managers see nothing (double safety — mount in OrgPane is already gated)
  if (!canManage) return null;
  if (isLoading) return null;

  const requests = data?.requests ?? [];
  if (requests.length === 0) return null;

  const busy = approveMut.isPending || denyMut.isPending;

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <Inbox className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
        <h2 className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          Clan Applications
        </h2>
        <span className="font-mono text-[10px] font-normal text-iron-500">
          {data?.pendingCount ?? requests.length} pending
        </span>
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 rounded bg-red-950/50 px-3 py-2 text-xs text-red-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          <span className="flex-1">{error}</span>
          <button
            onClick={() => setError('')}
            className="ml-2 text-red-500 hover:text-red-300"
            aria-label="Dismiss error"
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      )}

      <div className="space-y-2">
        {requests.map((req, i) => {
          const note = noteByReq[req.requestId] ?? '';
          return (
            <div
              key={req.requestId}
              className={cn(`${SETTLE} border border-iron-800/60 bg-iron-950 p-3`, busy && 'opacity-60')}
              style={settleDelay(i)}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-amber-700/30 bg-amber-950/20">
                  <Clock className="h-4 w-4 text-amber-400/70" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-sm font-black uppercase tracking-wide text-white">
                    {req.name}
                  </p>
                  <p className="mt-0.5 text-[10px] text-iron-500">
                    {req.requesterDisplayName ?? shortId(req.requesterDiscordId)} · {new Date(req.createdAt).toLocaleDateString()}
                    {req.gamertag && <> · {req.gamertag}</>}
                  </p>
                  {req.description && (
                    <p className="mt-2 whitespace-pre-line text-xs text-iron-300">{req.description}</p>
                  )}
                </div>
              </div>

              <textarea
                value={note}
                onChange={e => setNoteByReq(s => ({ ...s, [req.requestId]: e.target.value.slice(0, 500) }))}
                placeholder="Optional reason if declining (max 500 chars)…"
                disabled={busy}
                className="input mt-2 w-full min-h-[36px] text-[11px]"
              />

              <div className="mt-2 flex shrink-0 items-center gap-1.5">
                <button
                  onClick={() => approveMut.mutate(req.requestId)}
                  disabled={busy}
                  className="flex h-8 items-center gap-1 border border-emerald-500/50 bg-emerald-950/40 px-2.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-200 transition-all hover:border-emerald-400 hover:bg-emerald-900/50 hover:text-white disabled:opacity-40"
                >
                  {approveMut.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                  ) : (
                    <Check className="h-3 w-3" strokeWidth={3} />
                  )}
                  Approve
                </button>
                <button
                  onClick={() => denyMut.mutate({ requestId: req.requestId, reason: note.trim() || undefined })}
                  disabled={busy}
                  className="flex h-8 items-center gap-1 border border-red-500/50 bg-red-950/40 px-2.5 font-display text-[10px] font-black uppercase tracking-wider text-red-200 transition-all hover:border-red-400 hover:bg-red-900/50 hover:text-white disabled:opacity-40"
                >
                  {denyMut.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                  ) : (
                    <X className="h-3 w-3" strokeWidth={3} />
                  )}
                  Deny
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
